/**
 * ContextPromptViewer — read-only collapsed view of an AI-generated
 * "files context" prompt.
 *
 * Used in 4 places (tenant / bot / skill / bundle modals) to replace
 * the previous editable textareas. The user no longer authors or
 * triggers the prompt — it auto-syncs whenever the underlying files
 * or membership change (see auto-file-sync feature). This component
 * just renders what's there.
 *
 * Behavior:
 *   - Collapsed by default. Shows a single-line "Show AI-generated
 *     context" link with the prompt's character count as a hint.
 *   - Click toggles a read-only monospace preview with a Copy button.
 *   - No edit, no save, no regenerate button.
 *   - Empty / null prompt renders as a muted "(no context yet)" line
 *     instead of a useless toggle.
 */

import { useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import './ContextPromptViewer.css';

interface Props {
  prompt: string | null | undefined;
  /** Optional label for the toggle row — defaults to "AI-generated context". */
  label?: string;
  /** If true, the panel starts expanded. The chevron still toggles. */
  defaultOpen?: boolean;
}

export function ContextPromptViewer({ prompt, label, defaultOpen = false }: Props) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);

  const trimmed = (prompt ?? '').trim();
  const displayLabel = label ?? t('contextViewer.filesContextPrompt');

  // Render nothing when there's no content — the parent should call
  // this with an aggregated prompt (direct attachments + linked bundle
  // summaries). If the parent has nothing to show, the panel should
  // vanish entirely rather than emit a misleading "none yet" line.
  if (!trimmed) {
    return null;
  }

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(trimmed);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Best effort — dev tooling, silent failure is fine.
    }
  };

  return (
    <div className="ctx-prompt-viewer">
      <button
        type="button"
        className="ctx-prompt-viewer-toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className={`ctx-prompt-viewer-chevron ${open ? 'open' : ''}`}>▸</span>
        <span className="ctx-prompt-viewer-label">{displayLabel}</span>
        <span className="ctx-prompt-viewer-meta">
          {trimmed.length.toLocaleString()} chars
        </span>
      </button>

      {open && (
        <div className="ctx-prompt-viewer-body">
          <button
            type="button"
            className={`ctx-prompt-viewer-copy ${copied ? 'is-copied' : ''}`}
            onClick={handleCopy}
            title={copied ? t('common.copiedExclaim') : t('common.copyToClipboard')}
          >
            {copied ? `✓ ${t('common.copied')}` : `⧉ ${t('common.copy')}`}
          </button>
          <pre className="ctx-prompt-viewer-content">{trimmed}</pre>
        </div>
      )}
    </div>
  );
}

export default ContextPromptViewer;
